import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import API from '../api';
import { useAppDispatch } from '@app/store/store';
import { setCurrentUser } from '@store/reducers/auth';

// ─── Permissions ──────────────────────────────────────────────────────────────
interface Permissions {
  // Dashboard
  dashboard?: boolean;
  // Users
  users?: boolean;
  adduser?: boolean;
  edituser?: boolean;
  deleteuser?: boolean;
  manage_user_permission?: boolean;
  // Patients
  patients?: boolean;
  addpatient?: boolean;
  editpatient?: boolean;
  deletepatient?: boolean;
  // Investigation
  investigation?: boolean;
  // Doctor
  doctor?: boolean;
  add_doctor?: boolean;
  edit_doctor?: boolean;
  delete_doctor?: boolean;
  // Billing
  billing?: boolean;
  // Report
  report?: boolean;
  view_report?: boolean;
  delete_report?: boolean;
  // Finance
  payments?: boolean;
  finance?: boolean;
  income?: boolean;
  expense?: boolean;
  finance_report?: boolean;
  // Test Settings
  test_settings?: boolean;
  // Index signature so filterMenu can do permissions[key]
  [key: string]: boolean | undefined;
}

// ─── User ─────────────────────────────────────────────────────────────────────
interface User {
  id: number;
  user_type_id: number;
  lab_id: number;
  full_name: string;
  email: string;
  contact_no?: string;
  sex?: string;
  aadhar_no?: string;
  address?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  permissions: Permissions;
}

// ─── Context type ─────────────────────────────────────────────────────────────
interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  tokenExpired: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── JWT decoder (no library needed) ─────────────────────────────────────────
const decodeToken = (token: string) => {
  try {
    const base64Url  = token.split('.')[1];
    const base64     = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser]               = useState<User | null>(null);
  const [loading, setLoading]         = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);
  const dispatch        = useAppDispatch();
  const timeoutRef      = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ── Token expiration monitor ───────────────────────────────────────────────
  const setupTokenExpirationCheck = (token: string) => {
    if (timeoutRef.current)      clearTimeout(timeoutRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);

    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) {
      console.warn('Unable to decode token or token has no expiration');
      return;
    }

    const expirationTime     = decoded.exp * 1000;
    const timeUntilExpiration = expirationTime - Date.now();

    console.log(`Token will expire in ${Math.floor(timeUntilExpiration / 1000)} seconds`);

    if (timeUntilExpiration > 0) {
      timeoutRef.current = setTimeout(() => {
        console.warn('Token expired. Logging out...');
        handleLogout();
      }, timeUntilExpiration);

      checkIntervalRef.current = setInterval(() => {
        if (expirationTime - Date.now() <= 0) {
          console.warn('Token expired. Logging out...');
          handleLogout();
          if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
        }
      }, 60000);
    } else {
      console.warn('Token already expired');
      handleLogout();
    }
  };

  useEffect(() => {
    checkAuth();
    return () => {
      if (timeoutRef.current)      clearTimeout(timeoutRef.current);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, []);

  // ── Check persisted session ────────────────────────────────────────────────
  const checkAuth = async () => {
    const token      = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      setLoading(false);
      return;
    }

    try {
      const parsedUser: User = JSON.parse(storedUser);
      setUser(parsedUser);
      // FIX: Cast to any to avoid type mismatch with reducer's expected type
      dispatch(setCurrentUser(parsedUser as any));
      setTokenExpired(false);
      setupTokenExpirationCheck(token);
    } catch (error) {
      console.error('Auth check failed:', error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const login = async (email: string, password: string) => {
    try {
      const response       = await API.post('login', { email, password });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      setUser(user);
      setTokenExpired(false);
      // FIX: Cast to any to avoid type mismatch with reducer's expected type
      dispatch(setCurrentUser(user as any));
      setupTokenExpirationCheck(token);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // ── Logout ─────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setTokenExpired(true);
    // Also clear Redux so PrivateRoute/PublicRoute immediately see the user as logged out
    dispatch(setCurrentUser(null));
    if (timeoutRef.current)      clearTimeout(timeoutRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
  };

  const logout = () => handleLogout();

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    tokenExpired,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;